
import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import ProfileService from "services/profile-service";
import RefreshService from "services/refresh-service";

function SocialProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);

        const fbProfiles = await ProfileService.fetchProfile("Facebook").catch((err) => {
          console.warn("No Facebook profiles found or error occurred:", err);
          return [];
        });

        const ytProfiles = await ProfileService.fetchProfile("YouTube").catch((err) => {
          console.warn("No YouTube profiles found or error occurred:", err);
          return [];
        });

        const igProfiles = await ProfileService.fetchProfile("Instagram").catch((err) => {
          console.warn("No Instagram profiles found or error occurred:", err);
          return [];
        });

        const allProfiles = [...fbProfiles, ...ytProfiles, ...igProfiles];

        // Sort by creation date (newest first)
        allProfiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        setProfiles(allProfiles);
      } catch (err) {
        console.error("Error fetching social profiles:", err);
        setError("Failed to load social media profiles.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  const handleManualRefresh = async (platform, profileId) => {
    try {
      setRefreshing(profileId);
      await RefreshService.refreshProfile(platform, [], profileId, "Adhoc");

      // 🔁 Re-fetch profiles after refresh
      const fbProfiles = await ProfileService.fetchProfile("Facebook").catch(() => []);
      const ytProfiles = await ProfileService.fetchProfile("YouTube").catch(() => []);
      const igProfiles = await ProfileService.fetchProfile("Instagram").catch(() => []);
      const allProfiles = [...fbProfiles, ...ytProfiles, ...igProfiles];

      // Sort by creation date (newest first)
      allProfiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setProfiles(allProfiles);
      console.log(`Profile ${profileId} refreshed and data updated.`);
    } catch (error) {
      console.error("Manual refresh failed:", error);
    } finally {
      setRefreshing(null);
    }
  };



  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" mt={4}>
        <CircularProgress color="accent" />
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" mt={4}>
        <MDTypography color="error">{error}</MDTypography>
      </MDBox>
    );
  }

  const columns = [
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            PLATFORM
          </MDTypography>
        </MDBox>
      ),
      accessor: "platform",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            PROFILE NAME
          </MDTypography>
        </MDBox>
      ),
      accessor: "name",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            PROFILE ID
          </MDTypography>
        </MDBox>
      ),
      accessor: "profileId",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            FOLLOWERS / SUBSCRIBERS
          </MDTypography>
        </MDBox>
      ),
      accessor: "followers",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            PROFILE LINK
          </MDTypography>
        </MDBox>
      ),
      accessor: "link",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            UPDATED AT
          </MDTypography>
        </MDBox>
      ),
      accessor: "updatedAt",
      align: "left",
    },
    {
      Header: "ACTION",
      accessor: "action",
      align: "center",
    },
  ];

  const rows = profiles.map((profile) => ({
    platform: profile.platform,
    name: profile.name || "N/A",
    profileId: profile.profileId || profile.id || "N/A",
    followers: profile.count || 0,
    link: (
      <a href={profile.link} target="_blank" rel="noopener noreferrer">
        Visit
      </a>
    ),
    updatedAt: new Date(profile.updated_at).toLocaleString(),
    action: (
      <MDButton
        variant="contained"
        color="accent"
        size="small"
        onClick={() => handleManualRefresh(profile.platform, profile.id)}
        disabled={refreshing === profile.id}
      >
        {refreshing === profile.id ? "Refreshing..." : "Refresh"}
      </MDButton>
    ),
  }));

  return (
    <Card sx={{ mb: 3, mt: 7, overflow: "visible" }}>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDTypography variant="h6" color="white">
          Social Profiles Management
        </MDTypography>
      </MDBox>

      <MDBox p={2}>
        <DataTable
          canSearch={true}
          table={{ columns, rows }}
          showTotalEntries={false}
          isSorted={false}
          noEndBorder
          entriesPerPage={5}
        />
      </MDBox>
    </Card>
  );
}

export default SocialProfiles;
